/**
 * WorkRegistrationSection - Datos de Jornada (línea, servidor, bus, primas)
 * Sub-componente presentacional extraído de DayEditModal
 */
import { Sun, Moon, Bus, MapPin, Clock, Hash, Award, ChevronDown, ChevronUp } from 'lucide-react';

export default function WorkRegistrationSection({
    showWorkSection,
    setShowWorkSection,
    lineas,
    servidores,
    loadingServidores,
    selectedLinea,
    setSelectedLinea,
    selectedServidor,
    setSelectedServidor,
    turnoJornada,
    numeroBus,
    setNumeroBus,
    primasAplicables
}) {
    return (
        <div className="border rounded-2xl overflow-hidden transition-colors" style={{ borderColor: 'rgba(249, 115, 22, 0.2)' }}>
            <button
                id="tour-edit-jornada"
                onClick={() => setShowWorkSection(!showWorkSection)}
                className="w-full flex items-center justify-between p-3 bg-orange-500/10 hover:bg-orange-500/20 transition-colors"
            >
                <span className="flex items-center gap-2 text-orange-500 font-bold">
                    <Bus className="w-4 h-4" />
                    Datos de Jornada
                </span>
                {showWorkSection ? <ChevronUp className="w-4 h-4 text-orange-500" /> : <ChevronDown className="w-4 h-4 text-orange-500" />}
            </button>

            {showWorkSection && (
                <div className="p-3 space-y-3">
                    {/* Línea selector */}
                    <div>
                        <label className="block text-xs font-medium text-[var(--text-muted)] mb-1 flex items-center gap-1">
                            <MapPin className="w-3 h-3" /> Línea
                        </label>
                        <select
                            value={selectedLinea || ''}
                            onChange={(e) => {
                                setSelectedLinea(e.target.value ? parseInt(e.target.value, 10) : null);
                                setSelectedServidor(null);
                            }}
                            className="w-full px-3 py-3 rounded-xl border text-[var(--text-primary)] text-sm focus:outline-none focus:border-orange-500"
                            style={{ background: 'var(--bg-input)', borderColor: 'var(--border-color)' }}
                        >
                            <option value="" className="text-[var(--text-muted)]" style={{ background: 'var(--bg-secondary)' }}>Seleccionar línea...</option>
                            {lineas.map(l => (
                                <option key={l.id} value={l.id} className="text-[var(--text-primary)]" style={{ background: 'var(--bg-secondary)' }}>{l.codigo} - {l.nombre}</option>
                            ))}
                        </select>
                    </div>

                    {/* Servidor selector */}
                    <div>
                        <label className="block text-xs font-medium text-[var(--text-muted)] mb-1 flex items-center gap-1">
                            <Clock className="w-3 h-3" /> Servidor
                        </label>
                        <select
                            value={selectedServidor || ''}
                            onChange={(e) => setSelectedServidor(e.target.value ? parseInt(e.target.value, 10) : null)}
                            disabled={!selectedLinea || loadingServidores}
                            className="w-full px-3 py-3 rounded-xl border text-[var(--text-primary)] text-sm focus:outline-none focus:border-orange-500 disabled:opacity-50"
                            style={{ background: 'var(--bg-input)', borderColor: 'var(--border-color)' }}
                        >
                            <option value="" className="text-[var(--text-muted)]" style={{ background: 'var(--bg-secondary)' }}>
                                {loadingServidores ? 'Cargando...' : (!selectedLinea ? 'Selecciona línea primero' : 'Seleccionar servidor...')}
                            </option>
                            {servidores.map(s => (
                                <option key={s.id} value={s.id} className="text-[var(--text-primary)]" style={{ background: 'var(--bg-secondary)' }}>
                                    {s.codigo} {s.hora_inicio && s.hora_fin ? `(${s.hora_inicio?.slice(0, 5)} - ${s.hora_fin?.slice(0, 5)})` : ''}{s.reparto ? ' - REPARTO' : ''}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Turno auto-detected badge */}
                    {selectedServidor && turnoJornada && (
                        <div className={`flex items-center gap-2 p-3 rounded-lg border ${turnoJornada === 'M'
                            ? 'bg-yellow-500/10 border-yellow-500/30 text-yellow-400'
                            : 'bg-indigo-500/10 border-indigo-500/30 text-indigo-400'
                            }`}>
                            {turnoJornada === 'M' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                            <span className="text-sm font-medium">
                                Turno detectado: {turnoJornada === 'M' ? 'Mañana' : 'Tarde'}
                            </span>
                            <span className="text-xs opacity-70">(según calendario)</span>
                        </div>
                    )}

                    {/* Número de bus */}
                    <div>
                        <label className="block text-xs font-medium text-[var(--text-muted)] mb-1 flex items-center gap-1">
                            <Hash className="w-3 h-3" /> Nº Autobús
                        </label>
                        <input
                            type="text"
                            value={numeroBus}
                            onChange={(e) => setNumeroBus(e.target.value)}
                            placeholder="Ej: 172 o 172/175"
                            className="w-full px-3 py-3 rounded-xl border text-[var(--text-primary)] text-sm focus:outline-none focus:border-orange-500 placeholder-gray-500"
                            style={{ background: 'var(--bg-input)', borderColor: 'var(--border-color)' }}
                        />
                    </div>

                    {/* Primas aplicables */}
                    {primasAplicables.length > 0 && (
                        <div className="pt-2 border-t border-white/10">
                            <p className="text-xs text-[var(--text-muted)] mb-2 flex items-center gap-1">
                                <Award className="w-3 h-3" /> Primas aplicables:
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                                {primasAplicables.map(p => (
                                    <span
                                        key={p.id}
                                        className={`px-2 py-1 rounded-full text-xs font-medium ${p.es_global
                                            ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                                            : 'bg-green-500/20 text-green-400 border border-green-500/30'
                                            }`}
                                    >
                                        {p.nombre} ({p.importe}€)
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
